import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage, useAppStore } from '../stores/useAppStore';
import { SortDirection } from '@/services/enums';
import { useWatchlistSelfStore } from '../stores/useWatchlistSelfStore';
import { useWatchlistStore } from '../stores/useWatchlistStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import AddCoinDialog from '../components/AddCoinDialog';
import PageHeader from '../components/PageHeader';

type SortOption = {
  value: string;
  label: string;
  sortBy: string;
  sortOrder: SortDirection;
};

const SORT_OPTIONS: SortOption[] = [
  { value: 'default', label: '默认（添加时间）', sortBy: 'default', sortOrder: 'desc' },
  { value: 'fundingRate-desc', label: '资金费率（高→低）', sortBy: 'fundingRate', sortOrder: 'desc' },
  { value: 'fundingRate-asc', label: '资金费率（低→高）', sortBy: 'fundingRate', sortOrder: 'asc' },
  { value: 'minFundingRate-desc', label: '最低资金费率（高→低）', sortBy: 'minFundingRate', sortOrder: 'desc' },
  { value: 'minFundingRate-asc', label: '最低资金费率（低→高）', sortBy: 'minFundingRate', sortOrder: 'asc' },
  {
    value: 'fullyDilutedValuation-desc',
    label: '完全稀释估值（高→低）',
    sortBy: 'fullyDilutedValuation',
    sortOrder: 'desc',
  },
  {
    value: 'fullyDilutedValuation-asc',
    label: '完全稀释估值（低→高）',
    sortBy: 'fullyDilutedValuation',
    sortOrder: 'asc',
  },
  { value: 'totalVolume-desc', label: '总交易量（高→低）', sortBy: 'totalVolume', sortOrder: 'desc' },
  { value: 'totalVolume-asc', label: '总交易量（低→高）', sortBy: 'totalVolume', sortOrder: 'asc' },
  { value: 'circulationRate-desc', label: '流通率（高→低）', sortBy: 'circulationRate', sortOrder: 'desc' },
  { value: 'circulationRate-asc', label: '流通率（低→高）', sortBy: 'circulationRate', sortOrder: 'asc' },
  { value: 'bounce14d-desc', label: '14天反弹（高→低）', sortBy: 'bounce14d', sortOrder: 'desc' },
  { value: 'bounce14d-asc', label: '14天反弹（低→高）', sortBy: 'bounce14d', sortOrder: 'asc' },
  { value: 'bounce30d-desc', label: '30天反弹（高→低）', sortBy: 'bounce30d', sortOrder: 'desc' },
  { value: 'bounce30d-asc', label: '30天反弹（低→高）', sortBy: 'bounce30d', sortOrder: 'asc' },
  { value: 'pullback14d-desc', label: '14天回调（高→低）', sortBy: 'pullback14d', sortOrder: 'desc' },
  { value: 'pullback14d-asc', label: '14天回调（低→高）', sortBy: 'pullback14d', sortOrder: 'asc' },
  { value: 'pullback30d-desc', label: '30天回调（高→低）', sortBy: 'pullback30d', sortOrder: 'desc' },
  { value: 'pullback30d-asc', label: '30天回调（低→高）', sortBy: 'pullback30d', sortOrder: 'asc' },
  { value: 'volatility-desc', label: '波动率（高→低）', sortBy: 'volatility', sortOrder: 'desc' },
  { value: 'volatility-asc', label: '波动率（低→高）', sortBy: 'volatility', sortOrder: 'asc' },
  { value: 'cci-desc', label: 'CCI（高→低）', sortBy: 'cci', sortOrder: 'desc' },
  { value: 'cci-asc', label: 'CCI（低→高）', sortBy: 'cci', sortOrder: 'asc' },
  { value: 'symbol-asc', label: '币种名称（A→Z）', sortBy: 'symbol', sortOrder: 'asc' },
  { value: 'symbol-desc', label: '币种名称（Z→A）', sortBy: 'symbol', sortOrder: 'desc' },
  { value: 'upProbability-desc', label: '上涨概率（高→低）', sortBy: 'upProbability', sortOrder: 'desc' },
  { value: 'downProbability-desc', label: '下跌概率（高→低）', sortBy: 'downProbability', sortOrder: 'desc' },
  { value: 'momentumImpulse1h-desc', label: '脉冲（1小时）（高→低）', sortBy: 'momentumImpulse1h', sortOrder: 'desc' },
  { value: 'momentumImpulse1h-asc', label: '脉冲（1小时）（低→高）', sortBy: 'momentumImpulse1h', sortOrder: 'asc' },
];

const SORT_VALUE_STORAGE_KEY = 'watchlist_self_sort_value';
const REFRESH_FLAG_KEY = 'watchlist_self_refresh_flag';
const SCROLL_POSITION_KEY = 'watchlist_self_scroll_position';
const FROM_METRICS_ANALYSIS_KEY = 'watchlist_self_from_metrics_analysis'; // 标记是否从指标分析页面返回

const WatchlistSelf: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [sortValue, setSortValue] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(SORT_VALUE_STORAGE_KEY);
      if (stored && SORT_OPTIONS.some((opt) => opt.value === stored)) {
        return stored;
      }
    }
    return 'default';
  });
  const [searchInput, setSearchInput] = useState<string>(''); // 搜索输入框的值
  const [searchTerm, setSearchTerm] = useState<string>(''); // 实际用于搜索的关键词
  const [includeExtendedFields, setIncludeExtendedFields] = useState<boolean>(() => {
    // 从sessionStorage读取，默认false
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('watchlist_self_include_extended');
      return stored === 'true';
    }
    return false;
  });
  const [currentPageNum, setCurrentPageNum] = useState<number>(1);
  const [pageSize] = useState<number>(20); // 固定每页20条，不再允许修改
  const [viewportHeight, setViewportHeight] = useState<number>(window.innerHeight);
  const [headerHeight, setHeaderHeight] = useState<number>(56); // 头部高度（top-14 = 56px）
  const [searchBarHeight, setSearchBarHeight] = useState<number>(104); // 搜索栏高度（估算）
  const headerRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const setCurrentPage = useSetCurrentPage();
  const setPlaybackSymbol = useAppStore((state) => state.setPlaybackSymbol);
  const currentPage = useAppStore((state) => state.currentPage);
  const {
    items,
    isLoading,
    error,
    refreshWatchlistSelf,
    removeFromWatchlistSelf,
    removingSymbols,
    loadFromCache,
    saveToCache,
    pagination,
  } = useWatchlistSelfStore();
  const { addToWatchlist, checkIsWatched } = useWatchlistStore();
  const [addingToWatchlist, setAddingToWatchlist] = useState<Set<string>>(new Set());
  const hasLoadedRef = useRef(false);
  const refreshWatchlistSelfRef = useRef(refreshWatchlistSelf);
  const itemsLengthRef = useRef(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const isRestoringScrollRef = useRef(false);

  // 更新 ref 中的函数引用和数据长度
  refreshWatchlistSelfRef.current = refreshWatchlistSelf;
  itemsLengthRef.current = items.length;

  // 监听视口高度变化和头部/搜索栏高度（处理移动端键盘弹起）
  useEffect(() => {
    const updateHeights = () => {
      const newViewportHeight = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(newViewportHeight);

      // 动态计算头部和搜索栏的实际高度
      if (headerRef.current) {
        const newHeaderHeight = headerRef.current.offsetHeight;
        setHeaderHeight(newHeaderHeight);
      }
      if (searchBarRef.current) {
        const newSearchBarHeight = searchBarRef.current.offsetHeight;
        setSearchBarHeight(newSearchBarHeight);
      }
    };

    // 初始计算
    updateHeights();

    // 使用 visualViewport API（更好的移动端支持）
    if (window.visualViewport) {
      const handleViewportChange = () => {
        updateHeights();
      };
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);

      return () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
        window.visualViewport?.removeEventListener('scroll', handleViewportChange);
      };
    } else {
      // 降级方案：使用 window resize
      window.addEventListener('resize', updateHeights);
      window.addEventListener('orientationchange', updateHeights);

      return () => {
        window.removeEventListener('resize', updateHeights);
        window.removeEventListener('orientationchange', updateHeights);
      };
    }
  }, []);

  // 保存滚动位置
  const saveScrollPosition = () => {
    if (contentRef.current) {
      const scrollTop = contentRef.current.scrollTop;
      try {
        sessionStorage.setItem(SCROLL_POSITION_KEY, scrollTop.toString());
      } catch (error) {
        console.warn('保存滚动位置失败:', error);
      }
    }
  };

  // 恢复滚动位置
  const restoreScrollPosition = () => {
    if (contentRef.current && !isRestoringScrollRef.current) {
      try {
        const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
        if (savedPosition) {
          const scrollTop = parseInt(savedPosition, 10);
          if (!isNaN(scrollTop)) {
            isRestoringScrollRef.current = true;
            // 使用 setTimeout 确保 DOM 已渲染
            setTimeout(() => {
              if (contentRef.current) {
                contentRef.current.scrollTop = scrollTop;
                // 恢复后重置标记，允许下次保存
                setTimeout(() => {
                  isRestoringScrollRef.current = false;
                }, 100);
              }
            }, 100);
          }
        }
      } catch (error) {
        console.warn('恢复滚动位置失败:', error);
      }
    }
  };

  // 处理排序变化
  const handleSortChange = async (value: string) => {
    setSortValue(value);
    setCurrentPageNum(1); // 排序时重置到第一页
    try {
      sessionStorage.setItem(SORT_VALUE_STORAGE_KEY, value);
    } catch (err) {
      console.warn('保存排序选项失败:', err);
    }
    const selectedSort = SORT_OPTIONS.find((opt) => opt.value === value) || SORT_OPTIONS[0];
    try {
      await refreshWatchlistSelf(
        true,
        selectedSort.sortBy,
        selectedSort.sortOrder,
        1,
        pageSize,
        searchTerm,
        includeExtendedFields,
      );
    } catch (error) {
      console.error('排序失败:', error);
    }
  };

  // 处理页码变化
  const handlePageChange = async (newPage: number) => {
    if (newPage < 1 || (pagination && newPage > pagination.totalPages)) {
      return;
    }
    setCurrentPageNum(newPage);
    const selectedSort = SORT_OPTIONS.find((opt) => opt.value === sortValue) || SORT_OPTIONS[0];
    try {
      await refreshWatchlistSelf(
        true,
        selectedSort.sortBy,
        selectedSort.sortOrder,
        newPage,
        pageSize,
        searchTerm,
        includeExtendedFields,
      );
      // 滚动到顶部
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    } catch (error) {
      console.error('切换页码失败:', error);
    }
  };

  useEffect(() => {
    // 更新 items 长度到 ref
    itemsLengthRef.current = items.length;

    // 如果不在 watchlist-self 页面，直接返回
    if (currentPage !== 'watchlist-self') {
      return;
    }

    // 检查是否是从指标分析页面返回的
    const fromMetricsAnalysis = sessionStorage.getItem(FROM_METRICS_ANALYSIS_KEY) === 'true';

    // 如果是从指标分析页面返回
    if (fromMetricsAnalysis) {
      // 清除标记
      sessionStorage.removeItem(FROM_METRICS_ANALYSIS_KEY);

      // 如果已经加载过数据，直接使用现有数据，不请求服务器
      if (hasLoadedRef.current && itemsLengthRef.current > 0) {
        console.log(
          `[WatchlistSelf] 从指标分析页面返回，已加载过数据（${itemsLengthRef.current}条），使用现有数据，跳过服务器请求`,
        );
        // 恢复滚动位置
        restoreScrollPosition();
        return;
      }

      // 如果未加载过数据，优先从缓存加载（即使使用分页也尝试加载缓存）
      if (!hasLoadedRef.current) {
        const loadedFromCache = loadFromCache();
        if (loadedFromCache && items.length > 0) {
          console.log(`[WatchlistSelf] 从指标分析页面返回，从缓存加载数据成功（${items.length}条），跳过服务器请求`);
          hasLoadedRef.current = true;
          setLoading(false);
          // 恢复滚动位置
          restoreScrollPosition();
          return;
        }
      }

      // 如果缓存加载失败，仍然需要从服务器加载
      console.log(`[WatchlistSelf] 从指标分析页面返回，缓存加载失败，将从服务器加载`);
    }

    // 如果已经加载过数据且页面有内容，则不重新加载
    if (hasLoadedRef.current && itemsLengthRef.current > 0) {
      console.log(`[WatchlistSelf] 已加载过数据且有内容（${itemsLengthRef.current}条），跳过重新加载`);
      // 恢复滚动位置
      restoreScrollPosition();
      return;
    }

    // 检查是否是从"我的自选"按钮点击进来的（需要刷新数据）
    const shouldRefresh = sessionStorage.getItem(REFRESH_FLAG_KEY) === 'true';

    // 如果不是从按钮点击进入，尝试从缓存加载（仅当不使用分页时）
    if (!shouldRefresh && !hasLoadedRef.current && !pagination) {
      const loadedFromCache = loadFromCache();
      if (loadedFromCache && items.length > 0) {
        console.log(`[WatchlistSelf] 从缓存加载数据成功（${items.length}条），跳过服务器请求`);
        hasLoadedRef.current = true;
        setLoading(false);
        // 恢复滚动位置
        restoreScrollPosition();
        return;
      }
    }

    // 从服务器加载数据（首次加载或需要刷新时）
    const loadWatchlistSelf = async () => {
      // 防止重复调用
      if (hasLoadedRef.current && !shouldRefresh) {
        return;
      }

      hasLoadedRef.current = true;
      setLoading(true);

      // 如果是需要刷新的情况，清除标记
      if (shouldRefresh) {
        sessionStorage.removeItem(REFRESH_FLAG_KEY);
      }

      try {
        // 获取当前排序选项
        const currentSort = SORT_OPTIONS.find((opt) => opt.value === sortValue) || SORT_OPTIONS[0];
        // 使用 ref 中的函数引用，避免依赖项问题
        await refreshWatchlistSelfRef.current(
          true,
          currentSort.sortBy,
          currentSort.sortOrder,
          currentPageNum,
          pageSize,
          searchTerm,
          includeExtendedFields,
        );
      } catch (error) {
        console.error('加载自选列表失败:', error);
        hasLoadedRef.current = false; // 失败时重置，允许重试
      } finally {
        setLoading(false);
      }
    };

    loadWatchlistSelf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]); // 只依赖 currentPage，items.length 通过 ref 访问

  // 监听滚动事件，保存滚动位置
  useEffect(() => {
    if (currentPage !== 'watchlist-self' || !contentRef.current) {
      return;
    }

    const handleScroll = () => {
      // 如果正在恢复滚动位置，不保存
      if (isRestoringScrollRef.current) {
        return;
      }
      saveScrollPosition();
    };

    const contentElement = contentRef.current;
    contentElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
  }, [currentPage]);

  // 当数据加载完成后恢复滚动位置
  useEffect(() => {
    if (currentPage === 'watchlist-self' && !loading && !isLoading && items.length > 0) {
      // 延迟恢复，确保 DOM 已完全渲染
      const timer = setTimeout(() => {
        restoreScrollPosition();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [currentPage, loading, isLoading, items.length]);

  // 执行搜索
  const handleSearch = async () => {
    const trimmedSearch = searchInput.trim();
    setSearchTerm(trimmedSearch);
    setCurrentPageNum(1); // 搜索时重置到第一页

    const selectedSort = SORT_OPTIONS.find((opt) => opt.value === sortValue) || SORT_OPTIONS[0];
    try {
      await refreshWatchlistSelf(
        true,
        selectedSort.sortBy,
        selectedSort.sortOrder,
        1,
        pageSize,
        trimmedSearch,
        includeExtendedFields,
      );
    } catch (error) {
      console.error('搜索失败:', error);
    }
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleRemoveFromWatchlistSelf = async (symbol: string, instType: string, e?: React.MouseEvent) => {
    e?.stopPropagation(); // 阻止事件冒泡
    try {
      await removeFromWatchlistSelf(symbol, instType);
    } catch (error: any) {
      console.error('从自选中移除失败:', error.message);
    }
  };

  const handleAddToWatchlist = async (symbol: string, instType: string, e?: React.MouseEvent) => {
    e?.stopPropagation(); // 阻止事件冒泡
    const key = `${symbol}-${instType}`;

    // 检查是否已关注
    if (checkIsWatched(symbol, instType)) {
      return;
    }

    setAddingToWatchlist((prev) => new Set(prev).add(key));
    try {
      await addToWatchlist(symbol, instType);
    } catch (error: any) {
      console.error('添加关注失败:', error.message);
    } finally {
      setAddingToWatchlist((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleCardClick = (symbol: string, _instType: string) => {
    // 在跳转前保存当前数据到缓存
    if (items.length > 0) {
      saveToCache();
    }
    // 记录当前页面，用于返回
    sessionStorage.setItem('coin_metrics_previous_page', 'watchlist-self');
    // 设置标记，表示从自选页面跳转到指标分析页面
    sessionStorage.setItem(FROM_METRICS_ANALYSIS_KEY, 'true');
    setPlaybackSymbol(symbol);
    setCurrentPage('coin-metrics-analysis');
  };

  const formatNumber = (value: string | null | undefined): string => {
    if (!value) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    return num.toFixed(2);
  };

  // 直接使用 items，搜索由后端接口处理
  const filteredItems = items;

  // 同步分页信息到本地状态
  useEffect(() => {
    if (pagination && pagination.page !== currentPageNum) {
      setCurrentPageNum(pagination.page);
    }
  }, [pagination, currentPageNum]);

  return (
    <GradientBackground>
      <div className="min-h-screen" style={{ minHeight: `${viewportHeight}px` }}>
        <ParticleBackground />

        {/* 页面头部 */}
        <PageHeader
          containerRef={headerRef}
          title="我的自选"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={() => setIsAddDialogOpen(true)}
              className="w-9 h-9 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          }
        />

        {/* 排序和搜索栏 - 始终显示 */}
        <div
          ref={searchBarRef}
          className="fixed left-0 right-0 z-40 bg-black/10 backdrop-blur-sm border-b border-white/10 px-4 py-2 space-y-2"
          style={{ top: `${headerHeight}px` }}
        >
          <div className="flex items-center gap-2">
            <label className="text-white/70 text-sm whitespace-nowrap">排序:</label>
            <select
              value={sortValue}
              onChange={(e) => handleSortChange(e.target.value)}
              disabled={loading || isLoading}
              className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="bg-gray-800 text-white">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          {/* 搜索输入框和扩展字段复选框 - 同一行 */}
          <div className="flex items-center gap-2">
            {/* 搜索输入框 */}
            <span className="text-white/70 text-sm whitespace-nowrap">搜索:</span>
            <div className="relative flex-1">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchInputKeyDown}
                placeholder="币种名称"
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 pl-9 pr-20 text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40"
                // 移动端优化：防止键盘弹起时的布局问题
                inputMode="search"
                enterKeyHint="search"
                onFocus={(e) => {
                  // 当输入框获得焦点时，滚动到输入框位置，确保可见
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                {searchInput && (
                  <button
                    onClick={() => {
                      setSearchInput('');
                      setSearchTerm('');
                      setCurrentPageNum(1);
                      const selectedSort = SORT_OPTIONS.find((opt) => opt.value === sortValue) || SORT_OPTIONS[0];
                      refreshWatchlistSelf(
                        true,
                        selectedSort.sortBy,
                        selectedSort.sortOrder,
                        1,
                        pageSize,
                        '',
                        includeExtendedFields,
                      );
                    }}
                    className="text-white/40 hover:text-white/60 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            {/* 扩展字段复选框 */}
            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={includeExtendedFields}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIncludeExtendedFields(checked);
                  sessionStorage.setItem('watchlist_self_include_extended', String(checked));
                  // 重新加载数据
                  const selectedSort = SORT_OPTIONS.find((opt) => opt.value === sortValue) || SORT_OPTIONS[0];
                  refreshWatchlistSelf(
                    true,
                    selectedSort.sortBy,
                    selectedSort.sortOrder,
                    currentPageNum,
                    pageSize,
                    searchTerm,
                    checked,
                  );
                }}
                className="w-4 h-4 rounded border-white/20 bg-white/10 text-white focus:ring-2 focus:ring-white/30"
              />
              <span>显示扩展指标</span>
            </label>
          </div>
        </div>

        {/* 内容区域 */}
        <div
          ref={contentRef}
          className="px-4 overflow-y-auto"
          style={{
            // 内容区域从头部和搜索栏下方开始
            marginTop: `${headerHeight + searchBarHeight}px`,
            // 内容区域高度 = 视口高度 - 头部高度 - 搜索栏高度 - 分页栏高度（如果有）
            // 使用 max 确保高度不为负数
            height: `${Math.max(100, viewportHeight - headerHeight - searchBarHeight - (pagination ? 60 : 8))}px`,
            paddingBottom: pagination ? '60px' : '8px',
            boxSizing: 'border-box',
            // 确保内容可以完整滚动
            minHeight: 0,
            // 确保内容区域可以滚动
            position: 'relative',
            // 确保内容可以超出容器高度以便滚动
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch', // iOS 平滑滚动
          }}
        >
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <div className="text-red-300 text-sm">{error}</div>
            </div>
          )}

          {loading || isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">
                {searchTerm ? `未找到匹配 "${searchTerm}" 的币种` : '暂无自选的币种'}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item, index) => (
                <div
                  key={`${item.symbol}-${item.instType}`}
                  onClick={() => handleCardClick(item.symbol, item.instType)}
                  className={`bg-white/5 border border-white/20 rounded-xl p-4 text-white/90 mb-2 cursor-pointer hover:bg-white/10 transition-colors ${index === 0 ? 'mt-2' : ''}`}
                >
                  {/* 第一行：币种图标+名称+按钮组 */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center flex-1 min-w-0">
                      {item.imageThumb && (
                        <img
                          src={item.imageThumb}
                          alt={(item.coinBaseSymbol || item.symbol)?.toUpperCase()}
                          className="w-10 h-10 rounded-full mr-3 flex-shrink-0"
                        />
                      )}
                      <h3 className="font-bold text-white text-base truncate">
                        {(item.coinBaseSymbol || item.symbol)?.toUpperCase()}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* 关注按钮 */}
                      <button
                        onClick={(e) => handleAddToWatchlist(item.symbol, item.instType, e)}
                        disabled={
                          checkIsWatched(item.symbol, item.instType) ||
                          addingToWatchlist.has(`${item.symbol}-${item.instType}`)
                        }
                        className={`px-3 py-1.5 border rounded-lg transition-colors text-xs flex items-center gap-2 ${
                          checkIsWatched(item.symbol, item.instType)
                            ? 'bg-green-500/20 text-green-300 border-green-500/50 opacity-50 cursor-not-allowed'
                            : addingToWatchlist.has(`${item.symbol}-${item.instType}`)
                              ? 'bg-blue-500/20 text-blue-300 border-blue-500/50 opacity-50 cursor-not-allowed'
                              : 'bg-blue-500/20 text-blue-300 border-blue-500/50 hover:bg-blue-500/30'
                        }`}
                      >
                        {addingToWatchlist.has(`${item.symbol}-${item.instType}`) ? (
                          <>
                            <svg
                              className="animate-spin h-3 w-3"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span>关注中</span>
                          </>
                        ) : checkIsWatched(item.symbol, item.instType) ? (
                          '已关注'
                        ) : (
                          '关注'
                        )}
                      </button>
                      {/* 移除按钮 */}
                      <button
                        onClick={(e) => handleRemoveFromWatchlistSelf(item.symbol, item.instType, e)}
                        disabled={removingSymbols.has(`${item.symbol}-${item.instType}`)}
                        className={`px-3 py-1.5 bg-red-500/20 text-red-300 border border-red-500/50 rounded-lg hover:bg-red-500/30 transition-colors text-xs flex-shrink-0 flex items-center gap-2 ${
                          removingSymbols.has(`${item.symbol}-${item.instType}`) ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      >
                        {removingSymbols.has(`${item.symbol}-${item.instType}`) ? (
                          <>
                            <svg
                              className="animate-spin h-3 w-3"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            <span>移除中</span>
                          </>
                        ) : (
                          '移除'
                        )}
                      </button>
                    </div>
                  </div>

                  {/* 第二行：币种数据 */}
                  <div className="space-y-2 text-sm">
                    {item.fullyDilutedValuationUsd && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">完全稀释估值:</span>
                        <span className="text-white font-mono">${formatNumber(item.fullyDilutedValuationUsd)}</span>
                      </div>
                    )}
                    {item.totalVolumeUsd && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">总交易量:</span>
                        <span className="text-white font-mono">${formatNumber(item.totalVolumeUsd)}</span>
                      </div>
                    )}
                    {item.totalSupply && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">总供应量:</span>
                        <span className="text-white font-mono">{formatNumber(item.totalSupply)}</span>
                      </div>
                    )}
                    {item.circulatingSupply && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">流通供应量:</span>
                        <span className="text-white font-mono">{formatNumber(item.circulatingSupply)}</span>
                      </div>
                    )}
                    {/* 流通率 */}
                    {item.circulationRate !== null && item.circulationRate !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">流通率:</span>
                        <span className="text-white font-mono">{item.circulationRate.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* 资金费率（仅SWAP合约显示） */}
                    {item.instType === 'SWAP' && item.fundingRate !== null && item.fundingRate !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">资金费率:</span>
                        <span className={`font-mono ${item.fundingRate >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(item.fundingRate * 100).toFixed(4)}%
                        </span>
                      </div>
                    ) : null}

                    {/* 最低资金费率（仅SWAP合约显示） */}
                    {item.instType === 'SWAP' && item.minFundingRate !== null && item.minFundingRate !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">最低资金费率:</span>
                        <span className={`font-mono ${item.minFundingRate >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {(item.minFundingRate * 100).toFixed(4)}%
                        </span>
                      </div>
                    ) : null}

                    {/* 14天反弹幅度 */}
                    {item.bounce14d !== null && item.bounce14d !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">14天反弹:</span>
                        <span className="font-mono text-green-400">+{item.bounce14d.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* 30天反弹幅度 */}
                    {item.bounce30d !== null && item.bounce30d !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">30天反弹:</span>
                        <span className="font-mono text-green-400">+{item.bounce30d.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* 14天回调幅度 */}
                    {item.pullback14d !== null && item.pullback14d !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">14天回调:</span>
                        <span className="font-mono text-red-400">-{item.pullback14d.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* 30天回调幅度 */}
                    {item.pullback30d !== null && item.pullback30d !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">30天回调:</span>
                        <span className="font-mono text-red-400">-{item.pullback30d.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* 波动率 */}
                    {item.volatility !== null && item.volatility !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">波动率（1小时）:</span>
                        <span className="font-mono text-yellow-400">{item.volatility.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* CCI */}
                    {item.cci !== null && item.cci !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">CCI（1小时）:</span>
                        <span className="font-mono text-purple-400">{item.cci.toFixed(2)}</span>
                      </div>
                    ) : null}

                    {/* 脉冲指标（1小时） */}
                    {item.momentumImpulse1h !== null && item.momentumImpulse1h !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">脉冲（1小时）:</span>
                        <span
                          className={`font-mono ${item.momentumImpulse1h >= 0 ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {item.momentumImpulse1h.toFixed(4)}
                        </span>
                      </div>
                    ) : null}

                    {/* 上涨概率 */}
                    {item.upProbability !== null && item.upProbability !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">上涨概率:</span>
                        <span className="font-mono text-green-400">{item.upProbability.toFixed(2)}%</span>
                      </div>
                    ) : null}

                    {/* 下跌概率 */}
                    {item.downProbability !== null && item.downProbability !== undefined ? (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">下跌概率:</span>
                        <span className="font-mono text-red-400">{item.downProbability.toFixed(2)}%</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 分页组件 - 固定在底部 */}
        {pagination && (
          <div
            className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-between p-4 bg-black/20 backdrop-blur-md border-t border-white/10"
            style={{
              height: '60px',
              // 确保分页栏在头部和搜索栏之上
              zIndex: 30,
            }}
          >
            {/* 左侧：分页信息 */}
            <div className="text-sm text-white/60 whitespace-nowrap">
              共 {pagination.total} 条，第 {pagination.page} / {pagination.totalPages} 页
            </div>

            {/* 右侧：分页按钮 */}
            <div className="flex items-center gap-2">
              {/* 上一页按钮 */}
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={loading || isLoading || pagination.page <= 1}
                className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="上一页"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              {/* 下一页按钮 */}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={loading || isLoading || pagination.page >= pagination.totalPages}
                className="w-10 h-10 flex items-center justify-center bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="下一页"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* 添加币种对话框 */}
        <AddCoinDialog isOpen={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} />
      </div>
    </GradientBackground>
  );
};

export default WatchlistSelf;
